import { Divider, Stack, Typography } from '@mui/material';
import type React from 'react';
import type { DriverType } from '@repo/supergraph-schemas/marketplace/types';
import type { SummaryTableRowType } from '@repo/supergraph-schemas/roundup/base/types';
import ApyDriver from './ApyDriver';

interface CdApysDriverProps {
  dividerSpacing?: number;
  driver?: DriverType;
  oneYearApyEntity?: SummaryTableRowType['oneYearApyEntity'];
  threeYearApyEntity?: SummaryTableRowType['threeYearApyEntity'];
  fiveYearApyEntity?: SummaryTableRowType['fiveYearApyEntity'];
  /** Summary table: single APY column shows rate only (no "APY " prefix). */
  suppressApyTermPrefix?: boolean;
}

export const CdApysDriver = ({
  driver,
  oneYearApyEntity,
  threeYearApyEntity,
  fiveYearApyEntity,
  dividerSpacing = 3,
  suppressApyTermPrefix = false,
}: CdApysDriverProps): React.JSX.Element => {
  const traditionalTerms = [
    {
      ...oneYearApyEntity?.driverApy,
      label: '1-year APY',
    },
    {
      ...threeYearApyEntity?.driverApy,
      label: '3-year APY',
    },
    {
      ...fiveYearApyEntity?.driverApy,
      label: '5-year APY',
    },
  ];
  const apyDrivers = traditionalTerms?.filter(
    /*
      Only render drivers that have values attached to them. There are
      some uses cases where query0 can return empty drivers
    */
    driver => driver?.id?.includes('apy') && driver.value,
  );
  const hasMultiAPYDrivers = !!apyDrivers && apyDrivers.length > 0;
  // For summary table if there is only one APY value the APY prefix is rendered
  const finalDrivers = hasMultiAPYDrivers ? apyDrivers : [driver];

  if (!finalDrivers || finalDrivers.length === 0) {
    return <Typography>N/A</Typography>;
  }

  return (
    <Stack divider={<Divider sx={{ marginY: dividerSpacing }} />}>
      {finalDrivers?.map((driver, index) => {
        if (driver?.label && driver?.value) {
          return (
            <ApyDriver
              key={`${driver.id}-${index}`}
              termLength={driver.label}
              termLengthValue={driver.value}
              valueTooltip={driver.valueTooltip}
              suppressApyTermPrefix={suppressApyTermPrefix}
            />
          );
        }
        return <Typography key={`n-a-${index}`}>N/A</Typography>;
      })}
    </Stack>
  );
};
